import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  organizationApi,
  organizationProfessionalConfigApi,
} from 'src/api/organization';
import { specializationApi } from 'src/api/specialization';
import { visitCategoriesApi } from 'src/api/visit-categories';
import { visitTypesApi } from 'src/api/visit-types';
import { useUserContext } from 'src/auth/useUserContext';
import { useSnackbar } from 'src/components/snackbar';
import { mapCategoryNameToVisitCategory } from 'src/utils/mapCategoryNameToVisitCategory';
import { mapVisitTypeToLabel } from 'src/utils/mapVisitTypeToLabel';
import { GetVisitTypesDtoTypeEnum } from 'woofwoof-api';
import { useOrganizationContext } from '../../hooks/useOrganizationContext';
import OrganizationServiceManagement from './OrganizationServiceManagement';
import ServiceConfigSection from './ServiceConfigSection';
import { translateKey } from 'src/locales/translate';


type ServiceConfigData = {
  serviceId: string;
  customDuration?: number;
  notes?: string;
  isActive: boolean;
};

type CategoryOption = {
  value: string;
  label: string;
};

const VISIT_TYPES = [
  {
    value: GetVisitTypesDtoTypeEnum.Onsite,
    label: mapVisitTypeToLabel(GetVisitTypesDtoTypeEnum.Onsite),
  },
  {
    value: GetVisitTypesDtoTypeEnum.Video,
    label: mapVisitTypeToLabel(GetVisitTypesDtoTypeEnum.Video),
  },
  {
    value: GetVisitTypesDtoTypeEnum.Mobile,
    label: mapVisitTypeToLabel(GetVisitTypesDtoTypeEnum.Mobile),
  },
  // {
  //   value: GetVisitTypesDtoTypeEnum.Chat,
  //   label: mapVisitTypeToLabel(GetVisitTypesDtoTypeEnum.Chat),
  // },
];

export default function OrganizationVisitSettings() {
  const { organizationId, professionalId } = useParams<{
    organizationId: string;
    professionalId?: string;
  }>();
  const {
    isAdminRole,
    data: userData,
    isLoading: isLoadingUser,
    refetch: refetchUser,
  } = useUserContext();
  const { isProfessionalRoleSelected } = useOrganizationContext();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const targetProfessionalId =
    professionalId ||
    (isProfessionalRoleSelected && !isAdminRole ? userData?.id : undefined);
  const isEditingProfessionalConfig = !!targetProfessionalId;
  const isEditingOrgSettings = !isEditingProfessionalConfig;

  const { data: orgData, isLoading: isLoadingOrg } = useQuery(
    ['organization-booking', organizationId],
    () =>
      organizationApi.organizationControllerGetOrganizationById({
        id: organizationId!,
      }),
    {
      enabled: !!organizationId,
    },
  );

  // Fetch professional config for this user in this organization
  const { data: professionalConfigData, isLoading: isLoadingConfig } = useQuery(
    ['professionalConfig', organizationId, targetProfessionalId],
    () =>
      organizationProfessionalConfigApi.organizationProfessionalConfigControllerGetConfig(
        {
          organizationId: organizationId!,
          professionalId: targetProfessionalId!,
        },
      ),
    {
      enabled: !!organizationId && !!targetProfessionalId,
    },
  );

  const professionalConfig = professionalConfigData?.data;

  const organization = orgData?.data;
  // Show service config when editing professional settings
  const showServiceConfig = isEditingProfessionalConfig;
  const isOrgAdmin = isEditingOrgSettings;

  // Fetch filtered services for professionals (backend does the filtering)
  const { data: orgServicesData, isLoading: isLoadingOrgServices } = useQuery(
    ['organizationServices', organizationId, targetProfessionalId],
    async () => {
      if (!organizationId || !targetProfessionalId) return null;
      const response =
        await specializationApi.specializationControllerGetFilteredServicesForProfessional(
          {
            organizationId,
            professionalId: targetProfessionalId,
          },
        );
      return response;
    },
    {
      enabled: !!organizationId && !!targetProfessionalId,
    },
  );

  const allServices = orgServicesData?.data || [];

  // Fetch organization's enabled services (for professionals)
  // Filter to only show services that the organization admin has enabled
  const { data: visitTypesData } = useQuery(['allVisitTypes'], () =>
    visitTypesApi.getAllVisitTypes(),
  );

  const allVisitTypes = visitTypesData?.data || [];
  const { data: categoriesData } = useQuery(['visitCategories'], () =>
    visitCategoriesApi.visitCategoriesControllerGetAllVisitCategories(),
  );

  const apiCategories = categoriesData?.data || [];
  const categories: CategoryOption[] = apiCategories.map((cat: any) => ({
    value: cat.name,
    label: mapCategoryNameToVisitCategory(cat.name as any),
  }));

  // Helper function to check if a category is supported by a visit type
  const isCategorySupportedByVisitType = (
    visitType: string,
    category: string,
  ) => {
    const visitTypeData = allVisitTypes.find(
      (vt: any) => vt.type === visitType,
    );
    return visitTypeData?.categories?.includes(category) ?? false;
  };

  // Services are already filtered by backend
  const organizationServices = allServices;

  const [selectedVisitTypes, setSelectedVisitTypes] = useState<string[]>([]);
  const [visitTypeCategoryConfigs, setVisitTypeCategoryConfigs] = useState<
    Array<{ visitType: string; category: string }>
  >([]);
  const [serviceConfigs, setServiceConfigs] = useState<ServiceConfigData[]>([]);

  useEffect(() => {
    if (isEditingOrgSettings && organization) {
      setSelectedVisitTypes(organization.enabledVisitTypes || []);
      setVisitTypeCategoryConfigs(
        (organization as any).visitTypeCategoryConfigs || [],
      );
    } else if (professionalConfig) {
      setSelectedVisitTypes(professionalConfig.enabledVisitTypes || []);
      setVisitTypeCategoryConfigs(
        (professionalConfig as any).visitTypeCategoryConfigs || [],
      );
      // Map service configs from backend - serviceId is always a string
      const configs = (professionalConfig.serviceConfigs || []).map(
        (sc: any) => ({
          serviceId: sc.serviceId,
          customDuration: sc.customDuration,
          notes: sc.notes || '',
          isActive: sc.isActive !== false,
        }),
      );
      setServiceConfigs(configs);
    }
  }, [isEditingOrgSettings, organization, professionalConfig]);

  const updateOrgMutation = useMutation(
    (data: { enabledVisitTypes?: string[]; enabledCategories?: string[] }) =>
      organizationApi.organizationControllerUpdateOrganizationVisitSettings({
        id: organizationId!,
        updateVisitSettingsDto: data as any,
      }),
    {
      onSuccess: () => {
        enqueueSnackbar(translateKey("hardcoded.ustawienia_wizyt_zostaly_zaktualizowane"), {
          variant: 'success',
        });
        queryClient.invalidateQueries(['organization-booking', organizationId]);
        queryClient.invalidateQueries(['organization', organizationId]);
      },
      onError: () => {
        enqueueSnackbar(translateKey("hardcoded.blad_podczas_zapisywania_ustawien"), {
          variant: 'error',
        });
      },
    },
  );

  const updateProfessionalConfigMutation = useMutation(
    (data: {
      enabledVisitTypes: string[];
      enabledCategories?: string[];
      visitTypeCategoryConfigs?: Array<{ visitType: string; category: string }>;
      serviceConfigs: ServiceConfigData[];
    }) => {
      return organizationProfessionalConfigApi.organizationProfessionalConfigControllerSetConfig(
        {
          organizationId: organizationId!,
          professionalId: targetProfessionalId!,
          setProfessionalConfigDto: {
            enabledVisitTypes: data.enabledVisitTypes as any,
            enabledCategories: data.enabledCategories as any,
            visitTypeCategoryConfigs: data.visitTypeCategoryConfigs as any,
            serviceConfigs: data.serviceConfigs as any,
          },
        },
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['professionalConfig']);
        enqueueSnackbar(translateKey("hardcoded.twoje_ustawienia_zostaly_zaktualizowane"), {
          variant: 'success',
        });
      },
      onError: () => {
        enqueueSnackbar(translateKey("hardcoded.blad_podczas_zapisywania_ustawien"), {
          variant: 'error',
        });
      },
    },
  );

  const handleVisitTypeToggle = (value: string) => {
    setSelectedVisitTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleCategoryToggle = (visitType: string, category: string) => {
    setVisitTypeCategoryConfigs((prev) => {
      const exists = prev.some(
        (config) =>
          config.visitType === visitType && config.category === category,
      );
      if (exists) {
        return prev.filter(
          (config) =>
            !(config.visitType === visitType && config.category === category),
        );
      }
      return [...prev, { visitType, category }];
    });
  };

  const isCategoryEnabledForVisitType = (
    visitType: string,
    category: string,
  ) => {
    return visitTypeCategoryConfigs.some(
      (config) =>
        config.visitType === visitType && config.category === category,
    );
  };

  const handleSave = async () => {
    if (isEditingOrgSettings) {
      updateOrgMutation.mutate({
        enabledVisitTypes: selectedVisitTypes,
        visitTypeCategoryConfigs,
      } as any);
    } else {
      const allowedTypes = selectedVisitTypes.filter((type) =>
        organization?.enabledVisitTypes?.includes(type),
      );

      // Filter to only allow visit-type-category combinations that the org has enabled
      const orgVisitTypeCategoryConfigs =
        (organization as any)?.visitTypeCategoryConfigs || [];
      const allowedVisitTypeCategoryConfigs = visitTypeCategoryConfigs.filter(
        (config) =>
          orgVisitTypeCategoryConfigs.some(
            (orgConfig: any) =>
              orgConfig.visitType === config.visitType &&
              orgConfig.category === config.category,
          ),
      );

      updateProfessionalConfigMutation.mutate({
        enabledVisitTypes: allowedTypes,
        visitTypeCategoryConfigs: allowedVisitTypeCategoryConfigs,
        serviceConfigs,
      } as any);
    }
  };

  // Check loading state
  const isLoading =
    isLoadingOrg ||
    (isEditingProfessionalConfig && isLoadingConfig) ||
    isLoadingUser;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const isSaving =
    updateOrgMutation.isLoading || updateProfessionalConfigMutation.isLoading;

  return (
    <Stack spacing={3}>
      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Typy wizyt
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isOrgAdmin
                ? translateKey("hardcoded.wybierz_typy_wizyt_i_skonfiguruj_kategorie_dla_kazdego")
                : translateKey("hardcoded.wybierz_typy_wizyt_i_kategorie_ktore_obslugujesz")}
            </Typography>
          </Box>

          {/* Visit Types with nested Categories */}
          <Stack spacing={3}>
            {VISIT_TYPES.map((visitType) => {
              const isVisitTypeDisabled =
                !isOrgAdmin &&
                !organization?.enabledVisitTypes?.includes(visitType.value);
              const isVisitTypeEnabled = selectedVisitTypes.includes(
                visitType.value,
              );

              return (
                <Box key={visitType.value}>
                  {/* Visit Type Checkbox */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isVisitTypeEnabled}
                        data-testid={`visit-type-${visitType.value}`}
                        onChange={() => handleVisitTypeToggle(visitType.value)}
                        disabled={isVisitTypeDisabled}
                      />
                    }
                    label={
                      <Typography variant="subtitle1" fontWeight="medium">
                        {visitType.label}
                        {isVisitTypeDisabled && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >{translateKey("hardcoded.niedostepne_w_placowce")}</Typography>
                        )}
                      </Typography>
                    }
                  />

                  {/* Categories for this visit type */}
                  {isVisitTypeEnabled && (
                    <Box sx={{ pl: { xs: 1, md: 4 }, mt: 2 }}>
                      {(() => {
                        const supportedCategories = categories.filter(
                          (category: CategoryOption) =>
                            isCategorySupportedByVisitType(
                              visitType.value,
                              category.value,
                            ),
                        );

                        if (supportedCategories.length === 0) {
                          return (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic' }}
                            >{translateKey("hardcoded.brak_dostepnych_kategorii_dla_tego_typu_wizyty")}</Typography>
                          );
                        }

                        return (
                          <Stack spacing={2}>
                            {supportedCategories.map(
                              (category: CategoryOption) => {
                                // Professionals can only select combinations the org has enabled
                                // Check if this specific visitType+category combo is enabled by the org
                                const orgHasEnabled = (
                                  organization as any
                                )?.visitTypeCategoryConfigs?.some(
                                  (config: any) =>
                                    config.visitType === visitType.value &&
                                    config.category === category.value,
                                );
                                const isCategoryDisabled =
                                  !isOrgAdmin && !orgHasEnabled;
                                const isCategoryEnabled =
                                  isCategoryEnabledForVisitType(
                                    visitType.value,
                                    category.value,
                                  );

                                return (
                                  <Box key={category.value}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isCategoryEnabled}
                                          data-testid={`visit-cat-${visitType.value}-${category.value}`}
                                          onChange={() =>
                                            handleCategoryToggle(
                                              visitType.value,
                                              category.value,
                                            )
                                          }
                                          disabled={isCategoryDisabled}
                                        />
                                      }
                                      label={
                                        <span>
                                          {category.label}
                                          {isCategoryDisabled && (
                                            <Typography
                                              component="span"
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ ml: 1 }}
                                            >{translateKey("hardcoded.niedostepne_w_placowce")}</Typography>
                                          )}
                                        </span>
                                      }
                                    />

                                    {/* Service Management for this category */}
                                    {isOrgAdmin && isCategoryEnabled && (
                                      <Box
                                        sx={{
                                          pl: { xs: 0, md: 4 },
                                          mt: 2,
                                          mb: 2,
                                        }}
                                      >
                                        <OrganizationServiceManagement
                                          organizationId={organizationId!}
                                          enabledCategories={[category.value]}
                                          visitType={visitType.value}
                                        />
                                      </Box>
                                    )}

                                    {/* Service Configuration for professionals */}
                                    {showServiceConfig && isCategoryEnabled && (
                                      <Box
                                        sx={{
                                          pl: { xs: 0, md: 4 },
                                          mt: 2,
                                          mb: 2,
                                        }}
                                      >
                                        {(() => {
                                          const categoryServiceConfigs =
                                            serviceConfigs.filter((sc) =>
                                              organizationServices.some(
                                                (service: any) =>
                                                  (service._id ||
                                                    service.id) ===
                                                    sc.serviceId &&
                                                  service.category ===
                                                    category.value &&
                                                  service.visitType ===
                                                    visitType.value,
                                              ),
                                            );

                                          const categoryServices =
                                            organizationServices.filter(
                                              (service: any) =>
                                                service.category ===
                                                  category.value &&
                                                service.visitType ===
                                                  visitType.value,
                                            );

                                          if (categoryServices.length === 0) {
                                            return (
                                              <Alert
                                                severity="info"
                                                sx={{ mt: 1 }}
                                              >
                                                <Typography variant="body2">
                                                  <strong>{translateKey("hardcoded.brak_uslug_dla_kategorii")}{category.label}"
                                                  </strong>
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{ mt: 0.5 }}
                                                >{translateKey("hardcoded.administrator_placowki_nie_dodal_jeszcze_uslug_dla_tej")}</Typography>
                                              </Alert>
                                            );
                                          }

                                          return (
                                            <ServiceConfigSection
                                              categoryId={category.value}
                                              categoryName={category.label}
                                              visitType={visitType.value}
                                              services={organizationServices}
                                              serviceConfigs={
                                                categoryServiceConfigs
                                              }
                                              onServiceConfigsChange={(
                                                newConfigs,
                                              ) => {
                                                const otherConfigs =
                                                  serviceConfigs.filter(
                                                    (sc) =>
                                                      !organizationServices.some(
                                                        (service: any) =>
                                                          (service._id ||
                                                            service.id) ===
                                                            sc.serviceId &&
                                                          service.category ===
                                                            category.value &&
                                                          service.visitType ===
                                                            visitType.value,
                                                      ),
                                                  );
                                                setServiceConfigs([
                                                  ...otherConfigs,
                                                  ...newConfigs,
                                                ]);
                                              }}
                                            />
                                          );
                                        })()}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              },
                            )}
                          </Stack>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>

          {/* Show info when professional has no services available */}
          {showServiceConfig &&
            !isLoadingOrgServices &&
            organizationServices.length === 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>{translateKey("hardcoded.brak_dostepnych_uslug")}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{translateKey("hardcoded.administrator_placowki_nie_dodal_jeszcze_uslug_pasujacych_do")}</Typography>
              </Alert>
            )}

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              data-testid="visit-settings-save"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving && <CircularProgress size={20} />}
            >{translateKey("common.save")}</Button>
          </Box>
        </Stack>
      </Card>
    </Stack>
  );
}
